// Document storage for physical paint layers.
//
// A layer is saved as the full physical state of every simulation tile that
// holds anything (water, paper moisture, paint, pigment, dried film, or a
// dried spectral stack that differs from the substrate). Values are kept as
// float32 (lossless, so a reopened painting continues exactly where it was),
// then deflated and base64-encoded into the document's paintLayerStates.
// Pigments are stored by library id or custom colour, never by spectra.

import { deflateSync, inflateSync } from 'fflate';
import { PIGMENT_SLOTS, TILE_SIZE, scalarFieldNames, createPaintState } from '../core/PaintState.js';
import { BAND_COUNT } from '../pigments/ColourConversion.js';
import { resolvePigment, paperSpectrum } from '../pigments/PigmentLibrary.js';

export const LAYER_STATE_VERSION = 'vx-physical-1';
const SCALARS = scalarFieldNames();
// Per cell: scalars, 3 pigment layers x slots, spectral stack.
const VALUES_PER_CELL = SCALARS.length + 3 * PIGMENT_SLOTS + BAND_COUNT;

function toBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
}

function fromBase64(text) {
  const binary = typeof atob === 'function' ? atob(text) : Buffer.from(text, 'base64').toString('binary');
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

// readback: { fields: {name: Float32Array}, wetPig, depPig, bodyPig, dryRefl } (GPU engine readback shape)
export function encodeLayerState(readback, { width, height, slots, substrate, surface, docWidth, docHeight }) {
  const tilesX = Math.ceil(width / TILE_SIZE);
  const tilesY = Math.ceil(height / TILE_SIZE);
  const used = [];
  const layers = [readback.wetPig, readback.depPig, readback.bodyPig];
  const cellHasContent = (i) => {
    for (const name of SCALARS) if (readback.fields[name][i] !== 0) return true;
    for (const layer of layers) for (let k = 0; k < PIGMENT_SLOTS; k += 1) if (layer[k][i] !== 0) return true;
    for (let b = 0; b < BAND_COUNT; b += 1) if (Math.abs(readback.dryRefl[i * BAND_COUNT + b] - substrate[b]) > 1e-6) return true;
    return false;
  };
  for (let ty = 0; ty < tilesY; ty += 1) {
    for (let tx = 0; tx < tilesX; tx += 1) {
      let content = false;
      for (let y = ty * TILE_SIZE; y < Math.min(height, (ty + 1) * TILE_SIZE) && !content; y += 1) {
        for (let x = tx * TILE_SIZE; x < Math.min(width, (tx + 1) * TILE_SIZE); x += 1) {
          if (cellHasContent(y * width + x)) {
            content = true;
            break;
          }
        }
      }
      if (content) used.push(ty * tilesX + tx);
    }
  }
  // Field-major within each tile so similar values sit together (better compression).
  const cellsPerTile = TILE_SIZE * TILE_SIZE;
  const data = new Float32Array(used.length * cellsPerTile * VALUES_PER_CELL);
  let o = 0;
  for (const t of used) {
    const tx = t % tilesX;
    const ty = (t - tx) / tilesX;
    const cellAt = (c) => {
      const x = tx * TILE_SIZE + (c % TILE_SIZE);
      const y = ty * TILE_SIZE + Math.floor(c / TILE_SIZE);
      return x < width && y < height ? y * width + x : -1;
    };
    for (const name of SCALARS) for (let c = 0; c < cellsPerTile; c += 1) { const i = cellAt(c); data[o++] = i < 0 ? 0 : readback.fields[name][i]; }
    for (const layer of layers) for (let k = 0; k < PIGMENT_SLOTS; k += 1) for (let c = 0; c < cellsPerTile; c += 1) { const i = cellAt(c); data[o++] = i < 0 ? 0 : layer[k][i]; }
    for (let b = 0; b < BAND_COUNT; b += 1) for (let c = 0; c < cellsPerTile; c += 1) { const i = cellAt(c); data[o++] = i < 0 ? substrate[b] : readback.dryRefl[i * BAND_COUNT + b]; }
  }
  const packed = deflateSync(new Uint8Array(data.buffer), { level: 6 });
  return {
    version: LAYER_STATE_VERSION,
    simWidth: width,
    simHeight: height,
    docWidth,
    docHeight,
    tile: TILE_SIZE,
    bands: BAND_COUNT,
    slots: slots.map((p) => (p ? (p.code === 'custom' ? p.target : p.id) : null)),
    // (slots may be pigment objects or plain { id, code, target } records)
    surface,
    substrate: Array.from(substrate),
    tiles: used,
    data: toBase64(packed),
  };
}

// Resamples a CPU PaintState to another simulation size. Amounts are per cell
// area, so each target cell takes the mean of the source cells it covers
// (downsampling) or the source cell it falls in (upsampling).
export function resamplePaintState(src, width, height, surfaceData, substrate) {
  const dst = createPaintState(width, height, surfaceData, { paperSpectrum: substrate });
  dst.slots = src.slots.slice();
  dst.time = src.time || 0;
  const sx = src.width / width;
  const sy = src.height / height;
  const mean = (data, x0, x1, y0, y1, stride = 1, offset = 0) => {
    let sum = 0;
    for (let y = y0; y < y1; y += 1) for (let x = x0; x < x1; x += 1) sum += data[(y * src.width + x) * stride + offset];
    return sum / ((x1 - x0) * (y1 - y0));
  };
  for (let y = 0; y < height; y += 1) {
    const y0 = Math.min(src.height - 1, Math.floor(y * sy));
    const y1 = Math.max(y0 + 1, Math.min(src.height, Math.floor((y + 1) * sy)));
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.min(src.width - 1, Math.floor(x * sx));
      const x1 = Math.max(x0 + 1, Math.min(src.width, Math.floor((x + 1) * sx)));
      const i = y * width + x;
      for (const name of SCALARS) dst[name][i] = mean(src[name], x0, x1, y0, y1);
      for (const key of ['wetPig', 'depPig', 'bodyPig']) for (let k = 0; k < PIGMENT_SLOTS; k += 1) dst[key][k][i] = mean(src[key][k], x0, x1, y0, y1);
      for (let b = 0; b < BAND_COUNT; b += 1) dst.dryRefl[i * BAND_COUNT + b] = mean(src.dryRefl, x0, x1, y0, y1, BAND_COUNT, b);
    }
  }
  return dst;
}

// Returns a CPU PaintState (substrate filled, saved tiles restored) ready for engine.upload().
export function decodeLayerState(saved, surfaceData, substrate) {
  if (!saved || saved.version !== LAYER_STATE_VERSION) throw new Error('Not a physical paint layer state.');
  if (saved.bands !== BAND_COUNT || saved.tile !== TILE_SIZE) throw new Error('Physical paint layer uses an unsupported layout.');
  const width = saved.simWidth;
  const height = saved.simHeight;
  const state = createPaintState(width, height, surfaceData, { paperSpectrum: substrate });
  state.slots = (saved.slots || []).map((id) => (id ? resolvePigment(id) : null));
  while (state.slots.length < PIGMENT_SLOTS) state.slots.push(null);
  const data = new Float32Array(inflateSync(fromBase64(saved.data)).buffer);
  const tilesX = Math.ceil(width / TILE_SIZE);
  const cellsPerTile = TILE_SIZE * TILE_SIZE;
  const layers = [state.wetPig, state.depPig, state.bodyPig];
  let o = 0;
  for (const t of saved.tiles) {
    const tx = t % tilesX;
    const ty = (t - tx) / tilesX;
    const cellAt = (c) => {
      const x = tx * TILE_SIZE + (c % TILE_SIZE);
      const y = ty * TILE_SIZE + Math.floor(c / TILE_SIZE);
      return x < width && y < height ? y * width + x : -1;
    };
    for (const name of SCALARS) for (let c = 0; c < cellsPerTile; c += 1) { const i = cellAt(c); const v = data[o++]; if (i >= 0) state[name][i] = Number.isFinite(v) ? v : 0; }
    for (const layer of layers) for (let k = 0; k < PIGMENT_SLOTS; k += 1) for (let c = 0; c < cellsPerTile; c += 1) { const i = cellAt(c); const v = data[o++]; if (i >= 0) layer[k][i] = Number.isFinite(v) ? v : 0; }
    for (let b = 0; b < BAND_COUNT; b += 1) for (let c = 0; c < cellsPerTile; c += 1) { const i = cellAt(c); const v = data[o++]; if (i >= 0) state.dryRefl[i * BAND_COUNT + b] = Number.isFinite(v) ? v : substrate[b]; }
  }
  // Cells that still show the substrate they were saved with (for example the
  // white substrate of older saves) take the current paper.
  // Saves made before the substrate was recorded were all made over white.
  const savedSubstrate = Array.isArray(saved.substrate) && saved.substrate.length === BAND_COUNT ? saved.substrate : Array.from(paperSpectrum(0.97, 0));
  if (savedSubstrate.some((v, b) => Math.abs(v - substrate[b]) > 1e-6)) {
    for (let i = 0; i < width * height; i += 1) {
      let same = true;
      for (let b = 0; b < BAND_COUNT && same; b += 1) same = Math.abs(state.dryRefl[i * BAND_COUNT + b] - savedSubstrate[b]) <= 1e-5;
      if (same) state.dryRefl.set(substrate, i * BAND_COUNT);
    }
  }
  return state;
}
